import { useId, useState } from 'react';
import { useT } from '../../i18n';
import type { Vars } from '../../i18n';
import { ASSET_KINDS, type Asset, type AssetDraft } from '../../lib/api/assets';
import type { AssetMutationResult } from '../../state/useAssets';
import { BookingZoneEditor } from './BookingZoneEditor';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

// ─────────────────────────────────────────────────────────────────────
// THE EQUIPMENT REGISTRY — what a venue offers beside its courts
// (docs/domains/ASSET_BOOKING.md). Admin-mode dialog: the list of assets kept
// here (the centre's own and its clubs'), and a form for the ones this
// organization OWNS. A club's machine is listed and bookable here, but edited
// from the club's own portal — the server says so (404), and so does the row.
//
// PRICING REUSES THE ZONE EDITOR. An asset carries price zones in the booking
// engine's zone shape, so «Rediger prissoner» opens the same dialog the court
// zones use, on the asset's draft; one pricing model, one editor, one set of
// words.
// ─────────────────────────────────────────────────────────────────────

export interface AssetEditorProps {
  assets: Asset[];
  /** The centre's booking config — the holiday set the zone editor needs. */
  config?: any;
  onClose: () => void;
  /** Creates (no id) or updates (id) one asset. */
  onSave: (draft: AssetDraft & { id?: string }) => Promise<AssetMutationResult<Asset>>;
  /** Removes one asset; refused by the server when it has bookings. */
  onDelete: (id: string) => Promise<AssetMutationResult>;
}

type Translate = (key: any, vars?: Vars) => string;

/** The word for an asset category — a code on the wire, a label on screen. */
export function assetKindLabel(t: Translate, kind: string): string {
  switch (kind) {
    case 'ball_machine':
      return t('booking.assets.kind.ball_machine');
    case 'racket':
      return t('booking.assets.kind.racket');
    case 'camera':
      return t('booking.assets.kind.camera');
    default:
      return t('booking.assets.kind.other');
  }
}

/** The sentence for a registry refusal — codes from the server, words from the catalog. */
export function assetErrorMessage(t: Translate, r: AssetMutationResult<unknown>): string {
  if (r.code === 'asset_has_bookings') return t('booking.assets.error.asset_has_bookings');
  if (r.code === 'forbidden' && r.detail?.capability === 'booking.rules.manage') {
    return t('booking.assets.error.forbidden_pricing');
  }
  return t('booking.assets.error.generic', { error: String(r.error ?? '') });
}

/** One line for what an asset costs — zones if it has them, else the default price, else «free». */
export function assetPriceSummary(
  t: Translate,
  a: Pick<Asset, 'priceZones' | 'defaultPricePerHour'>,
): string {
  const zones = Array.isArray(a.priceZones) ? a.priceZones.length : 0;
  if (zones > 0) return t('booking.assets.zones', { n: String(zones) });
  if (!a.defaultPricePerHour) return t('booking.assets.free');
  return t('booking.assets.pricePerHour', { n: String(a.defaultPricePerHour) });
}

const BUFFER_OPTIONS = [0, 30, 60, 90];

interface Draft {
  id?: string;
  name: string;
  kind: string;
  description: string;
  active: boolean;
  bufferMin: number;
  defaultPricePerHour: number;
  priceZones: any[] | null;
}

const emptyDraft = (): Draft => ({
  name: '',
  kind: 'ball_machine',
  description: '',
  active: true,
  bufferMin: 0,
  defaultPricePerHour: 0,
  priceZones: null,
});

const draftOf = (a: Asset): Draft => ({
  id: a.id,
  name: a.name,
  kind: a.kind,
  description: a.description ?? '',
  active: a.active,
  bufferMin: a.bufferMin ?? 0,
  defaultPricePerHour: a.defaultPricePerHour ?? 0,
  priceZones: Array.isArray(a.priceZones) ? a.priceZones : null,
});

export function AssetEditor({
  assets,
  config = null,
  onClose,
  onSave,
  onDelete,
}: AssetEditorProps) {
  const t = useT();
  const uid = safeId('asset-editor', useId());
  const ids = {
    lead: `${uid}-lead`,
    name: `${uid}-name`,
    kind: `${uid}-kind`,
    description: `${uid}-description`,
    active: `${uid}-active`,
    buffer: `${uid}-buffer`,
    price: `${uid}-price`,
    error: `${uid}-error`,
  };

  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [showZones, setShowZones] = useState(false);

  const priceSummary = (a: Pick<Asset, 'priceZones' | 'defaultPricePerHour'>) =>
    assetPriceSummary(t, a);

  const save = async () => {
    if (!draft || !draft.name.trim()) return;
    setBusy(true);
    setError(null);
    const r = await onSave({
      ...(draft.id ? { id: draft.id } : {}),
      name: draft.name.trim(),
      kind: draft.kind,
      description: draft.description.trim() || null,
      active: draft.active,
      bufferMin: draft.bufferMin,
      defaultPricePerHour: draft.defaultPricePerHour,
      priceZones: draft.priceZones,
    });
    setBusy(false);
    if (r.success) setDraft(null);
    else setError(assetErrorMessage(t, r));
  };

  const remove = async (id: string) => {
    setBusy(true);
    setError(null);
    const r = await onDelete(id);
    setBusy(false);
    setConfirmDelete(null);
    if (!r.success) setError(assetErrorMessage(t, r));
  };

  return (
    <Dialog
      heading={t('booking.assets.title')}
      describedBy={ids.lead}
      onClose={onClose}
      className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5"
    >
      <p id={ids.lead} className="text-sm text-ink-2 mb-4">
        {t('booking.assets.lead')}
      </p>

      {error && (
        <div id={ids.error} role="alert" className="text-xs text-rose mb-3">
          {error}
        </div>
      )}

      {draft === null ? (
        <>
          {assets.length === 0 && (
            <p className="text-sm text-ink-3 mb-3">{t('booking.assets.empty')}</p>
          )}
          <ul className="flex flex-col gap-2 mb-4">
            {assets.map((a) => (
              <li
                key={a.id}
                className="flex items-start justify-between gap-3 rounded-xl border border-line px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-ink-1 truncate">{a.name}</span>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-ink-3">
                      {assetKindLabel(t, a.kind)}
                    </span>
                    {!a.active && (
                      <span className="text-[10px] font-mono uppercase tracking-wider rounded px-1 py-0.5 bg-amber-100 text-amber-800">
                        {t('booking.assets.inactive')}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ink-3 mt-0.5 flex flex-wrap gap-x-3">
                    <span>{priceSummary(a)}</span>
                    <span>
                      {a.bufferMin > 0
                        ? t('booking.assets.bufferMin', { n: String(a.bufferMin) })
                        : t('booking.assets.bufferNone')}
                    </span>
                    {!a.ownedHere && a.ownerName && (
                      <span>{t('booking.assets.ownedBy', { name: a.ownerName })}</span>
                    )}
                    {!a.atThisVenue && a.venueName && (
                      <span>{t('booking.assets.atVenue', { name: a.venueName })}</span>
                    )}
                  </div>
                  {confirmDelete === a.id && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
                      <span className="text-ink-2">
                        {t('booking.assets.deleteConfirm', { name: a.name })}
                      </span>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => remove(a.id)}
                        className="font-semibold rounded px-2 py-1 bg-danger text-white disabled:opacity-60"
                      >
                        {t('booking.assets.delete')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(null)}
                        className="rounded px-2 py-1 border border-line text-ink-2"
                      >
                        {t('booking.cancel')}
                      </button>
                    </div>
                  )}
                </div>
                {a.ownedHere ? (
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        setDraft(draftOf(a));
                      }}
                      className="text-xs font-semibold rounded-full px-3 py-1.5 border border-line text-ink-2 hover:bg-bg-soft"
                    >
                      {t('booking.assets.edit')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(a.id)}
                      className="text-xs rounded-full px-3 py-1.5 border border-line text-ink-3 hover:bg-bg-soft"
                    >
                      {t('booking.assets.delete')}
                    </button>
                  </div>
                ) : (
                  <span className="text-[11px] text-ink-3 flex-shrink-0 max-w-[12rem] text-right">
                    {t('booking.assets.notOwner', { name: a.ownerName ?? '' })}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => {
                setError(null);
                setDraft(emptyDraft());
              }}
              className="text-sm font-semibold rounded-full px-4 py-2 bg-lime text-ink-1 shadow hover:shadow-md"
            >
              {t('booking.assets.add')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-sm rounded-full px-4 py-2 border border-line text-ink-2"
            >
              {t('booking.close')}
            </button>
          </div>
        </>
      ) : (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <div>
            <label
              htmlFor={ids.name}
              className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
            >
              {t('booking.assets.name')}
            </label>
            <input
              id={ids.name}
              className="input w-full"
              value={draft.name}
              maxLength={80}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor={ids.kind}
                className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
              >
                {t('booking.assets.kind')}
              </label>
              <select
                id={ids.kind}
                className="input w-full"
                value={draft.kind}
                onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
              >
                {ASSET_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {assetKindLabel(t, k)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor={ids.buffer}
                className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
              >
                {t('booking.assets.buffer')}
              </label>
              <select
                id={ids.buffer}
                className="input w-full"
                value={String(draft.bufferMin)}
                onChange={(e) => setDraft({ ...draft, bufferMin: Number(e.target.value) })}
              >
                {BUFFER_OPTIONS.map((m) => (
                  <option key={m} value={String(m)}>
                    {m === 0
                      ? t('booking.assets.bufferNone')
                      : t('booking.assets.bufferMin', { n: String(m) })}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label
              htmlFor={ids.description}
              className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
            >
              {t('booking.assets.description')}
            </label>
            <textarea
              id={ids.description}
              className="input w-full"
              rows={2}
              maxLength={500}
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-ink-2 select-none">
            <input
              id={ids.active}
              type="checkbox"
              checked={draft.active}
              onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
            />
            {t('booking.assets.active')}
          </label>

          <div className="rounded-xl border border-line p-3">
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-2">
              {t('booking.assets.pricing')}
            </div>
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <label htmlFor={ids.price} className="block text-xs text-ink-2 mb-1">
                  {t('booking.assets.defaultPrice')}
                </label>
                <input
                  id={ids.price}
                  type="number"
                  min={0}
                  className="input w-32"
                  value={draft.defaultPricePerHour}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      defaultPricePerHour: Math.max(0, Math.trunc(Number(e.target.value) || 0)),
                    })
                  }
                />
              </div>
              <div className="text-xs text-ink-3 pb-2">
                {Array.isArray(draft.priceZones) && draft.priceZones.length > 0
                  ? t('booking.assets.zones', { n: String(draft.priceZones.length) })
                  : t('booking.assets.noZones')}
              </div>
              <button
                type="button"
                onClick={() => setShowZones(true)}
                className="text-xs font-semibold rounded-full px-3 py-1.5 border border-line text-ink-2 hover:bg-bg-soft"
              >
                {t('booking.assets.editZones')}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                setDraft(null);
                setError(null);
              }}
              className="text-sm rounded-full px-4 py-2 border border-line text-ink-2"
            >
              {t('booking.assets.back')}
            </button>
            <button
              type="submit"
              disabled={busy || !draft.name.trim()}
              className="text-sm font-semibold rounded-full px-4 py-2 bg-lime text-ink-1 shadow hover:shadow-md disabled:opacity-60"
            >
              {busy ? t('booking.assets.saving') : t('booking.assets.save')}
            </button>
          </div>
        </form>
      )}

      {draft && showZones && (
        <BookingZoneEditor
          zones={draft.priceZones ?? []}
          defaultPricePerHour={draft.defaultPricePerHour}
          config={config}
          onClose={() => setShowZones(false)}
          onSave={(zones, defaultPrice) => {
            setDraft({
              ...draft,
              priceZones: zones.length > 0 ? zones : null,
              defaultPricePerHour: defaultPrice,
            });
            setShowZones(false);
          }}
        />
      )}
    </Dialog>
  );
}
